import { httpClient } from './http-client';
import { auth } from './firebase';
import { logout } from './adapters/auth';
import { queryClient } from '@/app/providers/queryClient';
import { useAuthStore } from '@/app/store/auth.store';
import { toast } from '@/app/store/toast.store';
import { i18n } from '@/shared/i18n';

/**
 * Wire request/response interceptors onto `httpClient`. Called once at app bootstrap (from `main.tsx`).
 *
 * - Request:  attach `Authorization: Bearer <token>` from the Firebase SDK.
 * - Response: on a 401 that is NOT the boot-refresh / logout call, clear auth state + cached queries and let
 *   the router guards do the navigation. We deliberately do NOT `window.location.href = '/login'` (mirrors
 *   admin-web): a hard redirect bounces public pages (`/signup`, `/reset-password`) off the screen before they
 *   mount, and it ignores whether the user was even logged in. Instead `ProtectedRoute` sends protected pages
 *   to `/login` while public pages stay put.
 *
 *   The `X-Refresh-Auth` guard is what makes that safe. Both the once-per-load boot `POST /auth/refresh` and
 *   the `POST /auth/logout` call carry that header. Skipping them here means:
 *     1. a fresh visitor's boot-refresh 401 no longer logs anything out or redirects (fixes `/signup` being
 *        unreachable for invited users), and
 *     2. the logout POST's own 401 can't re-enter this handler and loop.
 */
export function setupInterceptors() {
  httpClient.interceptors.request.use(async (config) => {
    // Attach the current Firebase ID token (auto-refreshing) on every request — mirror admin-web. Pre-auth
    // calls (login) run before sign-in, so currentUser is null and no Authorization header is added. The
    // backend-mediated login exchanges its custom token via signInWithCustomToken, after which currentUser
    // is set (see api/adapters/auth.ts).
    const user = auth.currentUser;
    if (user) {
      config.headers.Authorization = `Bearer ${await user.getIdToken()}`;
    }
    // Dev-only locale toggle: ask the BE for English error messages only under `?locale=en`.
    // Deliberately a custom header (not Accept-Language, which browsers auto-send from the OS language
    // and would leak English to real users); absent → BE resolves Korean. Mirrors admin-web.
    if (i18n.resolvedLanguage === 'en') {
      config.headers['X-Locale'] = 'en';
    }
    return config;
  });

  httpClient.interceptors.response.use(
    (response) => response,
    (error) => {
      // The boot-refresh and logout calls tag themselves with X-Refresh-Auth; their 401 is expected and must
      // NOT trigger a logout/redirect (and must not let logout's 401 re-enter this handler).
      const isRefreshOrLogout = error.config?.headers?.['X-Refresh-Auth'] === '1';
      if (error.response?.status === 401 && !isRefreshOrLogout) {
        // Read the live-session flag BEFORE we clear it, so we tell a mid-session expiry (was logged in) from a
        // cold-boot miss (never logged in → stay silent, no toast).
        const wasAuthenticated = useAuthStore.getState().isAuthenticated;
        const code = error.response?.data?.error?.code;
        if (code === 'SESSION_SUPERSEDED') {
          // Single-session eviction: a newer login on another device revoked this session.
          toast.error(i18n.t('login.errors.duplicateSession', { ns: 'auth' }));
        } else if (wasAuthenticated) {
          // A live session's token expired / was invalidated mid-use → friendly "log in again" notice.
          toast.error(i18n.t('login.errors.sessionExpired', { ns: 'auth' }));
        }
        if (wasAuthenticated) {
          // Revoke the server-side refresh cookie + drop the Firebase session (logout() signs out in finally),
          // so a reload can't re-establish a rejected session. Swallow its own rejection — the X-Refresh-Auth
          // guard above already keeps its 401 from re-entering here.
          void logout().catch(() => {});
        }
        // Clear local auth + cached queries; the router guards handle navigation (no hard redirect).
        useAuthStore.getState().logout();
        queryClient.clear();
      }
      return Promise.reject(error);
    },
  );
}
